import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputAdornment,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  KeyboardArrowRight as ChevronRightIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Save as SaveIcon,
  Label as KeywordIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { PageHeader } from '@/components/common';
import MetricCard from '@/components/MetricCard/MetricCard';
import { DateRangePicker } from '@/components/DateRangePicker';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  StyledTablePagination,
  StyledTableSortLabel,
  Table,
  TableCell,
  TableRow,
} from '@/components/StyledTable';
import TrendChart from '@/components/TrendChart/TrendChart';
import {
  KeywordStats,
  CategoryBreakdown,
  CampaignBreakdown,
  AdBreakdown
} from '@/services/rsoc.service';
import {
  useKeywordStats,
  useCategoryBreakdown,
  useCampaigns,
  useAds,
  useCategoryKeywords,
  useCategoryKeywordDaily,
  useCampaignKeywords,
  useCampaignKeywordDaily,
  useAdKeywords,
  useAdKeywordDaily,
} from '@/hooks/api/useRsocData';
import { exportToCSV } from '@/utils/csv.utils';
import dayjs from 'dayjs';
import {
  BreakdownType,
  KeywordBreakdown,
  RsocDailyBreakdown,
  ColumnVisibility,
  SavedView,
} from '@/types';

// Helper component for rendering nested keywords using React Query
interface NestedKeywordsProps {
  parentType: 'category' | 'campaign' | 'ad';
  parentId: string;
  parentKey: string;
  dateRange: { from: string; to: string };
  expandedRows: Set<string>;
  toggleRow: (key: string) => void;
  visibleColumns: ColumnVisibility;
}

const NestedKeywords = ({
  parentType,
  parentId,
  parentKey,
  dateRange,
  expandedRows,
  toggleRow,
  visibleColumns,
}: NestedKeywordsProps) => {
  // Fetch keywords based on parent type - use all hooks with conditional enabling
  const categoryQuery = useCategoryKeywords(parentId, dateRange.from, dateRange.to, {
    enabled: parentType === 'category',
  });
  const campaignQuery = useCampaignKeywords(parentId, dateRange.from, dateRange.to, {
    enabled: parentType === 'campaign',
  });
  const adQuery = useAdKeywords(parentId, dateRange.from, dateRange.to, {
    enabled: parentType === 'ad',
  });

  const keywords: KeywordBreakdown[] =
    parentType === 'category' ? categoryQuery.data || [] :
    parentType === 'campaign' ? campaignQuery.data || [] :
    adQuery.data || [];

  return (
    <>
      {keywords.map((kw) => {
        const kwKey = `${parentKey}:${kw.keyword}`;
        const isKwExpanded = expandedRows.has(kwKey);

        return (
          <Fragment key={kwKey}>
            <TableRow hover sx={{ backgroundColor: '#fff' }}>
              <TableCell sx={{ pl: 4 }}>
                <IconButton size="small" onClick={() => toggleRow(kwKey)}>
                  {isKwExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </IconButton>
              </TableCell>
              {visibleColumns.keyword && <TableCell><Typography variant="body2" fontWeight={500}>{kw.keyword}</Typography></TableCell>}
              {visibleColumns.category && <TableCell></TableCell>}
              {visibleColumns.clicks && <TableCell align="right"><Typography variant="body2" color="info.main">{Number(kw.total_clicks || kw.clicks).toLocaleString()}</Typography></TableCell>}
              {visibleColumns.days && <TableCell align="center"><Typography variant="body2">{kw.days_active}</Typography></TableCell>}
              {visibleColumns.revenue && <TableCell align="right"><Typography variant="body2" color="success.main" fontWeight={500}>${Number(kw.total_revenue || kw.revenue).toFixed(2)}</Typography></TableCell>}
              {visibleColumns.rpc && <TableCell align="right"><Typography variant="body2" fontWeight={600} color="primary">${Number(kw.avg_rpc || kw.rpc).toFixed(4)}</Typography></TableCell>}
              {visibleColumns.campaigns && <TableCell></TableCell>}
              {visibleColumns.ads && <TableCell></TableCell>}
              {visibleColumns.trendPercentage && <TableCell align="center">-</TableCell>}
              {visibleColumns.maxRPC && <TableCell align="right">-</TableCell>}
              {visibleColumns.rpcTrend && <TableCell>-</TableCell>}
            </TableRow>

            {/* Daily data for this keyword */}
            {isKwExpanded && (
              <NestedDaily
                parentType={parentType}
                parentId={parentId}
                keyword={kw.keyword}
                kwKey={kwKey}
                dateRange={dateRange}
                visibleColumns={visibleColumns}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

// Helper component for rendering daily data using React Query
interface NestedDailyProps {
  parentType: 'category' | 'campaign' | 'ad';
  parentId: string;
  keyword: string;
  kwKey: string;
  dateRange: { from: string; to: string };
  visibleColumns: ColumnVisibility;
}

const NestedDaily = ({
  parentType,
  parentId,
  keyword,
  visibleColumns,
  dateRange,
}: NestedDailyProps) => {
  // Fetch daily data based on parent type - use all hooks with conditional enabling
  const categoryQuery = useCategoryKeywordDaily(parentId, keyword, dateRange.from, dateRange.to, {
    enabled: parentType === 'category',
  });
  const campaignQuery = useCampaignKeywordDaily(parentId, keyword, dateRange.from, dateRange.to, {
    enabled: parentType === 'campaign',
  });
  const adQuery = useAdKeywordDaily(parentId, keyword, dateRange.from, dateRange.to, {
    enabled: parentType === 'ad',
  });

  const daily: RsocDailyBreakdown[] =
    parentType === 'category' ? categoryQuery.data || [] :
    parentType === 'campaign' ? campaignQuery.data || [] :
    adQuery.data || [];

  return (
    <>
      {daily.map((day, dayIndex) => (
        <TableRow key={dayIndex} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          <TableCell sx={{ pl: 8 }}></TableCell>
          {visibleColumns.keyword && <TableCell><Typography variant="body2" color="text.secondary" fontSize="0.8125rem">{day.date}</Typography></TableCell>}
          {visibleColumns.category && <TableCell></TableCell>}
          {visibleColumns.clicks && <TableCell align="right"><Typography variant="body2" fontSize="0.8125rem">{Number(day.clicks).toLocaleString()}</Typography></TableCell>}
          {visibleColumns.days && <TableCell></TableCell>}
          {visibleColumns.revenue && <TableCell align="right"><Typography variant="body2" fontSize="0.8125rem">${Number(day.revenue).toFixed(2)}</Typography></TableCell>}
          {visibleColumns.rpc && <TableCell align="right"><Typography variant="body2" fontSize="0.8125rem" fontWeight={500}>${Number(day.rpc).toFixed(4)}</Typography></TableCell>}
          {visibleColumns.campaigns && <TableCell></TableCell>}
          {visibleColumns.ads && <TableCell></TableCell>}
          {visibleColumns.trendPercentage && <TableCell></TableCell>}
          {visibleColumns.maxRPC && <TableCell></TableCell>}
          {visibleColumns.rpcTrend && <TableCell></TableCell>}
        </TableRow>
      ))}
    </>
  );
};

const RSOCKeywordsPage = () => {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [breakdownType, setBreakdownType] = useState<BreakdownType>('none');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [orderBy, setOrderBy] = useState<string>('avg_rpc');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<ColumnVisibility>({
    keyword: true,
    category: true,
    clicks: true,
    days: true,
    revenue: true,
    rpc: true,
    campaigns: true,
    ads: true,
    trendPercentage: true,
    maxRPC: true,
    rpcTrend: true,
  });

  // Views
  const [currentViewName, setCurrentViewName] = useState('');
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);

  // Date range - default last 7 days
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const start = new Date(yesterday);
  start.setDate(start.getDate() - 6);

  const [dateRange, setDateRange] = useState({
    from: dayjs(start).format('YYYY-MM-DD'),
    to: dayjs(yesterday).format('YYYY-MM-DD'),
  });

  // Data - can be any of the breakdown types
  type DataRow = KeywordStats | CategoryBreakdown | CampaignBreakdown | AdBreakdown;

  // Load saved views from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('rsoc_saved_views');
    if (saved) {
      try {
        setSavedViews(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load saved views', e);
      }
    }
  }, []);

  // Debounced search term
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // React Query hooks for data fetching
  const keywordStatsQuery = useKeywordStats(
    dateRange.from,
    dateRange.to,
    debouncedSearch || undefined
  );
  const categoryQuery = useCategoryBreakdown(dateRange.from, dateRange.to, {
    enabled: breakdownType === 'category',
  });
  const campaignsQuery = useCampaigns(dateRange.from, dateRange.to, {
    enabled: breakdownType === 'campaigns',
  });
  const adsQuery = useAds(dateRange.from, dateRange.to, {
    enabled: breakdownType === 'ads',
  });

  // Select the correct data based on breakdown type
  const data: DataRow[] = (() => {
    switch (breakdownType) {
      case 'category':
        return categoryQuery.data || [];
      case 'campaigns':
        return campaignsQuery.data || [];
      case 'ads':
        return adsQuery.data || [];
      default:
        return keywordStatsQuery.data || [];
    }
  })();

  // Always use keyword stats for summary (regardless of breakdown)
  const keywordStats = useMemo(() => keywordStatsQuery.data || [], [keywordStatsQuery.data]);

  // Loading and error states
  const loading = keywordStatsQuery.isLoading || categoryQuery.isLoading || campaignsQuery.isLoading || adsQuery.isLoading;
  const errorObj = keywordStatsQuery.error || categoryQuery.error || campaignsQuery.error || adsQuery.error;
  const error = errorObj ? (errorObj as Error & { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to fetch data' : '';

  // Clear expanded rows when breakdown type changes
  useEffect(() => {
    setExpandedRows(new Set());
  }, [breakdownType, dateRange]);

  // Helper to get row key based on breakdown type
  const getRowKey = (row: DataRow): string => {
    if ('keyword' in row) return row.keyword;
    const categoryRow = row as CategoryBreakdown;
    if (categoryRow.category !== undefined) return categoryRow.category || 'Unknown';
    const campaignRow = row as CampaignBreakdown;
    if (campaignRow.campaign_id !== undefined) return campaignRow.campaign_id;
    const adRow = row as AdBreakdown;
    if (adRow.ad_id !== undefined) return adRow.ad_id;
    return '';
  };

  // Helper to get row display name based on breakdown type
  const getRowName = (row: DataRow): string => {
    if ('keyword' in row) return row.keyword;
    const categoryRow = row as CategoryBreakdown;
    if (categoryRow.category !== undefined) return categoryRow.category || 'Unknown';
    const campaignRow = row as CampaignBreakdown;
    if (campaignRow.campaign_id !== undefined) return campaignRow.campaign_name || campaignRow.campaign_id;
    const adRow = row as AdBreakdown;
    if (adRow.ad_id !== undefined) return adRow.ad_name || adRow.ad_id;
    return '';
  };

  // Toggle row expansion
  const toggleRow = (rowKey: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  // Handle sort
  const handleSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      const aValue = (a as unknown as Record<string, unknown>)[orderBy];
      const bValue = (b as unknown as Record<string, unknown>)[orderBy];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return order === 'asc' ? -1 : 1;
      if (aStr > bStr) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, orderBy, order]);

  // Paginate data
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Summary statistics - always based on keyword stats
  const summaryStats = useMemo(() => {
    if (keywordStats.length === 0) {
      return {
        totalKeywords: 0,
        highestRPC: { value: '0.00', keyword: '-' },
        topCategory: { name: '-', revenue: '0.00' },
        avgRPC: '0.0000',
      };
    }

    const highestRPCRow = keywordStats.reduce((max, row) =>
      (row.avg_rpc || 0) > (max.avg_rpc || 0) ? row : max, keywordStats[0]
    );

    const categoryRevenue: Record<string, number> = {};
    keywordStats.forEach((row) => {
      const cat = row.category || 'Unknown';
      categoryRevenue[cat] = (categoryRevenue[cat] || 0) + parseFloat(String(row.total_revenue || 0));
    });
    const topCat = Object.entries(categoryRevenue).reduce(
      (max, [cat, rev]) => (rev > max[1] ? [cat, rev] : max),
      ['Unknown', 0]
    );

    const totalRevenue = keywordStats.reduce((sum, row) => sum + parseFloat(String(row.total_revenue || 0)), 0);
    const totalClicks = keywordStats.reduce((sum, row) => sum + parseInt(String(row.total_clicks || 0)), 0);
    const avgRPC = totalClicks > 0 ? totalRevenue / totalClicks : 0;

    return {
      totalKeywords: keywordStats.length,
      highestRPC: {
        value: parseFloat(String(highestRPCRow.avg_rpc || 0)).toFixed(2),
        keyword: highestRPCRow.keyword,
      },
      topCategory: {
        name: topCat[0],
        revenue: topCat[1].toFixed(2),
      },
      avgRPC: avgRPC.toFixed(4),
    };
  }, [keywordStats]);

  // Export to CSV
  const handleExportCSV = () => {
    exportToCSV({
      data,
      columns: [
        { header: 'Name', getValue: (row) => getRowName(row) },
        { header: 'Category/ID', getValue: (row) => ('category' in row ? row.category : ('campaign_id' in row ? row.campaign_id : '')) || '' },
        { header: 'Revenue', getValue: (row) => row.total_revenue || 0 },
        { header: 'Clicks', getValue: (row) => row.total_clicks || 0 },
        { header: 'RPC', getValue: (row) => row.avg_rpc || 0 },
        { header: 'Days Active', getValue: (row) => row.days_active || 0 },
        { header: 'Keywords', getValue: (row) => ('keyword_count' in row ? row.keyword_count : '') || '' },
        { header: 'Campaigns', getValue: (row) => ('campaign_count' in row ? row.campaign_count : '') || '' },
        { header: 'Ads', getValue: (row) => ('ad_count' in row ? row.ad_count : '') || '' },
      ],
      filename: `rsoc-${breakdownType}-${dateRange.from}-to-${dateRange.to}`,
    });
  };

  // Save current view
  const saveView = () => {
    if (!currentViewName.trim()) {
      alert('Please enter a view name');
      return;
    }

    const newView: SavedView = {
      name: currentViewName,
      columns: visibleColumns,
      breakdownType,
    };

    const updated = [...savedViews.filter(v => v.name !== currentViewName), newView];
    setSavedViews(updated);
    localStorage.setItem('rsoc_saved_views', JSON.stringify(updated));
    setCurrentViewName('');
    alert(`View "${newView.name}" saved!`);
  };

  // Load saved view
  const loadView = (viewName: string) => {
    const view = savedViews.find(v => v.name === viewName);
    if (view) {
      setVisibleColumns(view.columns);
      setBreakdownType(view.breakdownType);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <PageHeader
        title="RSOC Keywords"
        subtitle="Revenue Share on Click - Keyword Performance Tracking"
      />

      {/* Summary Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Keywords"
            value={summaryStats.totalKeywords}
            color="primary"
            icon={<KeywordIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Highest RPC"
            value={`$${summaryStats.highestRPC.value}`}
            color="success"
            icon={<TrendingUpIcon />}
            subtitle={summaryStats.highestRPC.keyword}
            subtitleTitle={summaryStats.highestRPC.keyword}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Top Category"
            value={`$${summaryStats.topCategory.revenue}`}
            color="info"
            icon={<CategoryIcon />}
            subtitle={summaryStats.topCategory.name}
            subtitleTitle={summaryStats.topCategory.name}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Average RPC"
            value={`$${summaryStats.avgRPC}`}
            color="secondary"
            icon={<AnalyticsIcon />}
          />
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Controls Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Date, Search, and Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <TextField
              size="small"
              placeholder="Search keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={breakdownType !== 'none'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250, flexGrow: 1 }}
            />
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV} disabled={loading || data.length === 0}>
              Export CSV
            </Button>
          </Box>

          {/* Breakdown Type Selector */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', py: 2 }}>
            <FormControl component="fieldset">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600}>
                  Breakdown by:
                </Typography>
                <RadioGroup row value={breakdownType} onChange={(e) => setBreakdownType(e.target.value as BreakdownType)}>
                  <FormControlLabel value="none" control={<Radio size="small" />} label="Daily (default)" />
                  <FormControlLabel value="category" control={<Radio size="small" />} label="Category" />
                  <FormControlLabel value="campaigns" control={<Radio size="small" />} label="Campaigns" />
                  <FormControlLabel value="ads" control={<Radio size="small" />} label="Ads" />
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>

          {/* Column Visibility */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600}>
                Columns:
              </Typography>
              {Object.entries(visibleColumns).map(([col, visible]) => (
                <FormControlLabel
                  key={col}
                  control={
                    <Checkbox
                      size="small"
                      checked={visible}
                      onChange={(e) => setVisibleColumns(prev => ({ ...prev, [col]: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body2">{col}</Typography>}
                />
              ))}
            </Box>
          </Box>

          {/* View Management */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600}>
                Views:
              </Typography>
              <TextField
                size="small"
                placeholder="View name..."
                value={currentViewName}
                onChange={(e) => setCurrentViewName(e.target.value)}
                sx={{ minWidth: 180 }}
              />
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveView}
                disabled={!currentViewName.trim()}
              >
                Save View
              </Button>
              {savedViews.length > 0 && (
                <>
                  <Box sx={{ borderLeft: 1, borderColor: 'divider', height: 32, mx: 1 }} />
                  <Select
                    size="small"
                    displayEmpty
                    defaultValue=""
                    onChange={(e) => e.target.value && loadView(e.target.value as string)}
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="" disabled>
                      <Typography variant="body2" color="text.secondary">
                        Load saved view...
                      </Typography>
                    </MenuItem>
                    {savedViews.map((view) => (
                      <MenuItem key={view.name} value={view.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{view.name}</Typography>
                          <Chip
                            label={view.breakdownType === 'none' ? 'Daily' : view.breakdownType}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Data Table */}
      {loading && data.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '10px', boxShadow: '0 10px 30px rgba(146,153,184,0.15)', overflow: 'hidden' }}>
          <StyledTableContainer>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}></TableCell>
                  {visibleColumns.keyword && (
                    <TableCell sx={{ minWidth: 200 }}>
                      <StyledTableSortLabel active={orderBy === 'keyword'} direction={orderBy === 'keyword' ? order : 'asc'} onClick={() => handleSort('keyword')}>
                        Keyword
                      </StyledTableSortLabel>
                    </TableCell>
                  )}
                  {visibleColumns.category && (
                    <TableCell sx={{ minWidth: 150 }}>
                      <StyledTableSortLabel active={orderBy === 'category'} direction={orderBy === 'category' ? order : 'asc'} onClick={() => handleSort('category')}>
                        Category
                      </StyledTableSortLabel>
                    </TableCell>
                  )}
                  {visibleColumns.clicks && (
                    <TableCell align="right" sx={{ width: 100 }}>
                      <StyledTableSortLabel active={orderBy === 'total_clicks'} direction={orderBy === 'total_clicks' ? order : 'asc'} onClick={() => handleSort('total_clicks')}>
                        Clicks
                      </StyledTableSortLabel>
                    </TableCell>
                  )}
                  {visibleColumns.days && <TableCell align="center" sx={{ width: 80 }}>Days</TableCell>}
                  {visibleColumns.revenue && (
                    <TableCell align="right" sx={{ width: 120 }}>
                      <StyledTableSortLabel active={orderBy === 'total_revenue'} direction={orderBy === 'total_revenue' ? order : 'asc'} onClick={() => handleSort('total_revenue')}>
                        Revenue
                      </StyledTableSortLabel>
                    </TableCell>
                  )}
                  {visibleColumns.rpc && (
                    <TableCell align="right" sx={{ width: 100 }}>
                      <StyledTableSortLabel active={orderBy === 'avg_rpc'} direction={orderBy === 'avg_rpc' ? order : 'asc'} onClick={() => handleSort('avg_rpc')}>
                        RPC
                      </StyledTableSortLabel>
                    </TableCell>
                  )}
                  {visibleColumns.campaigns && <TableCell align="center" sx={{ width: 100 }}>Campaigns</TableCell>}
                  {visibleColumns.ads && <TableCell align="center" sx={{ width: 80 }}>Ads</TableCell>}
                  {visibleColumns.trendPercentage && <TableCell align="center" sx={{ width: 150 }}>Trend %</TableCell>}
                  {visibleColumns.maxRPC && <TableCell align="right" sx={{ width: 100 }}>Max RPC</TableCell>}
                  {visibleColumns.rpcTrend && <TableCell sx={{ width: 250 }}>RPC Trend</TableCell>}
                </TableRow>
              </StyledTableHead>
              <StyledTableBody>
                {/* Daily/None View: Keywords → Daily */}
                {breakdownType === 'none' && paginatedData.map((row) => {
                  const rowKey = getRowKey(row);
                  const rowName = getRowName(row);
                  const isExpanded = expandedRows.has(rowKey);
                  const hasDaily = 'daily' in row && row.daily && row.daily.length > 0;

                  return (
                    <Fragment key={rowKey}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleRow(rowKey)}>
                            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                          </IconButton>
                        </TableCell>
                        {visibleColumns.keyword && <TableCell><Typography variant="body2" fontWeight={500}>{rowName}</Typography></TableCell>}
                        {visibleColumns.category && <TableCell><Typography variant="body2" color="text.secondary">{'category' in row ? row.category : '-'}</Typography></TableCell>}
                        {visibleColumns.clicks && <TableCell align="right"><Typography variant="body2" color="info.main">{Number(row.total_clicks).toLocaleString()}</Typography></TableCell>}
                        {visibleColumns.days && <TableCell align="center"><Typography variant="body2">{row.days_active}</Typography></TableCell>}
                        {visibleColumns.revenue && <TableCell align="right"><Typography variant="body2" color="success.main" fontWeight={500}>${Number(row.total_revenue).toFixed(2)}</Typography></TableCell>}
                        {visibleColumns.rpc && <TableCell align="right"><Typography variant="body2" fontWeight={600} color="primary">${Number(row.avg_rpc).toFixed(4)}</Typography></TableCell>}
                        {visibleColumns.campaigns && <TableCell align="center"><Typography variant="body2">{'campaign_count' in row ? row.campaign_count : '-'}</Typography></TableCell>}
                        {visibleColumns.ads && <TableCell align="center"><Typography variant="body2">{'ad_count' in row ? row.ad_count : '-'}</Typography></TableCell>}
                        {visibleColumns.trendPercentage && (
                          <TableCell align="center">
                            {hasDaily && row.daily!.length > 1 ? (
                              <Typography variant="body2" color={row.daily![row.daily!.length - 1].rpc > row.daily![0].rpc ? 'success.main' : 'error.main'}>
                                {(((row.daily![row.daily!.length - 1].rpc - row.daily![0].rpc) / row.daily![0].rpc) * 100).toFixed(1)}%
                              </Typography>
                            ) : '-'}
                          </TableCell>
                        )}
                        {visibleColumns.maxRPC && (
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {hasDaily ? `$${Math.max(...row.daily!.map(d => d.rpc)).toFixed(4)}` : '-'}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.rpcTrend && (
                          <TableCell>
                            {hasDaily ? (
                              <TrendChart
                                data={row.daily!.map(d => ({ date: d.date, value: d.rpc }))}
                                height={32}
                                useNormalization
                                colorThresholds={[
                                  { value: 0.6, color: '#10b981' },
                                  { value: 0.4, color: '#22c55e' },
                                  { value: 0.25, color: '#f59e0b' },
                                  { value: 0, color: '#ef4444' },
                                ]}
                                valueFormatter={(v) => `$${v.toFixed(4)}`}
                              />
                            ) : '-'}
                          </TableCell>
                        )}
                      </TableRow>
                      {isExpanded && hasDaily && row.daily!.map((day, dayIndex) => (
                        <TableRow key={`${rowKey}-${dayIndex}`} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                          <TableCell></TableCell>
                          {visibleColumns.keyword && <TableCell><Typography variant="body2" color="text.secondary" fontSize="0.8125rem">{day.date}</Typography></TableCell>}
                          {visibleColumns.category && <TableCell></TableCell>}
                          {visibleColumns.clicks && <TableCell align="right"><Typography variant="body2" fontSize="0.8125rem">{Number(day.clicks).toLocaleString()}</Typography></TableCell>}
                          {visibleColumns.days && <TableCell></TableCell>}
                          {visibleColumns.revenue && <TableCell align="right"><Typography variant="body2" fontSize="0.8125rem">${Number(day.revenue).toFixed(2)}</Typography></TableCell>}
                          {visibleColumns.rpc && <TableCell align="right"><Typography variant="body2" fontSize="0.8125rem" fontWeight={500}>${Number(day.rpc).toFixed(4)}</Typography></TableCell>}
                          {visibleColumns.campaigns && <TableCell></TableCell>}
                          {visibleColumns.ads && <TableCell></TableCell>}
                          {visibleColumns.trendPercentage && <TableCell></TableCell>}
                          {visibleColumns.maxRPC && <TableCell></TableCell>}
                          {visibleColumns.rpcTrend && <TableCell></TableCell>}
                        </TableRow>
                      ))}
                    </Fragment>
                  );
                })}

                {/* Category/Campaign/Ad View: Parent → Keywords → Daily (3 levels) */}
                {breakdownType !== 'none' && paginatedData.map((row) => {
                  const parentKey = getRowKey(row);
                  const parentName = getRowName(row);
                  const isParentExpanded = expandedRows.has(parentKey);

                  const parentType = breakdownType === 'category' ? 'category' as const : breakdownType === 'campaigns' ? 'campaign' as const : 'ad' as const;
                  const parentId = breakdownType === 'category' ? (row as CategoryBreakdown).category || 'Unknown' :
                                   breakdownType === 'campaigns' ? (row as CampaignBreakdown).campaign_id :
                                   (row as AdBreakdown).ad_id;

                  return (
                    <Fragment key={parentKey}>
                      {/* Level 1: Category/Campaign/Ad */}
                      <TableRow hover sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleRow(parentKey)}>
                            {isParentExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                          </IconButton>
                        </TableCell>
                        {visibleColumns.keyword && <TableCell><Typography variant="body2" fontWeight={600}>{parentName}</Typography></TableCell>}
                        {visibleColumns.category && <TableCell><Typography variant="body2" color="text.secondary">{breakdownType === 'campaigns' || breakdownType === 'ads' ? parentId : ''}</Typography></TableCell>}
                        {visibleColumns.clicks && <TableCell align="right"><Typography variant="body2" color="info.main">{Number(row.total_clicks).toLocaleString()}</Typography></TableCell>}
                        {visibleColumns.days && <TableCell align="center"><Typography variant="body2">{row.days_active}</Typography></TableCell>}
                        {visibleColumns.revenue && <TableCell align="right"><Typography variant="body2" color="success.main" fontWeight={600}>${Number(row.total_revenue).toFixed(2)}</Typography></TableCell>}
                        {visibleColumns.rpc && <TableCell align="right"><Typography variant="body2" fontWeight={700} color="primary">${Number(row.avg_rpc).toFixed(4)}</Typography></TableCell>}
                        {visibleColumns.campaigns && <TableCell align="center"><Typography variant="body2">{'campaign_count' in row ? row.campaign_count : '-'}</Typography></TableCell>}
                        {visibleColumns.ads && <TableCell align="center"><Typography variant="body2">{'ad_count' in row ? row.ad_count : '-'}</Typography></TableCell>}
                        {visibleColumns.trendPercentage && <TableCell align="center">-</TableCell>}
                        {visibleColumns.maxRPC && <TableCell align="right">-</TableCell>}
                        {visibleColumns.rpcTrend && <TableCell>-</TableCell>}
                      </TableRow>

                      {/* Level 2 & 3: Keywords and Daily using React Query */}
                      {isParentExpanded && (
                        <NestedKeywords
                          parentType={parentType}
                          parentId={parentId}
                          parentKey={parentKey}
                          dateRange={dateRange}
                          expandedRows={expandedRows}
                          toggleRow={toggleRow}
                          visibleColumns={visibleColumns}
                        />
                      )}
                    </Fragment>
                  );
                })}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No data found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </StyledTableBody>
            </Table>
          </StyledTableContainer>
          <StyledTablePagination
            rowsPerPageOptions={[25, 50, 100, 200]}
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default RSOCKeywordsPage;
